import { PreflightLogo } from 'design-system';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { createPreflightCoreApiClient } from 'preflight-core';
import { PropsWithChildren, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { AUTH_TOKEN_KEY, AuthProvider, getSsoToken } from 'sso';

import { preflightApiClient } from '../api/client';
import { TrackingEvent, useEventTracking } from '../contexts/tracking-context';
import { signInNext, signOutNext } from './user-api';

const BASE_URL = process.env.NEXT_PUBLIC_SSO_API_BASE_URL ?? '';
const PRODUCT = process.env.NEXT_PUBLIC_SSO_PRODUCT ?? '';

const preflightCoreApiClient = createPreflightCoreApiClient(preflightApiClient);

const IS_USER_SIGNED_UP_COOKIE_NAME = 'isUserSignedUp';

function getDateAYearFromNow() {
  const now = new Date();
  return new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
}

const LinkWrapper = ({
  href,
  children,
  email,
}: PropsWithChildren<{ href: string; email?: string }>) => {
  const { query } = useRouter();
  return (
    <Link
      href={{
        pathname: href,
        query: Object.assign(
          {},
          email ? { email } : {},
          query.redirectTo ? { redirectTo: query.redirectTo } : {},
        ),
      }}
      passHref
    >
      {children}
    </Link>
  );
};

export const PreflightAuthContextConfiguration = (
  props: React.PropsWithChildren<{
    domain: string | undefined;
  }>,
) => {
  const { trackEvent, specialEvents } = useEventTracking();
  const [cookies, setCookie] = useCookies([IS_USER_SIGNED_UP_COOKIE_NAME]);
  const isUserSignedUp = useMemo(() => {
    return cookies[IS_USER_SIGNED_UP_COOKIE_NAME] === 'true';
  }, [cookies]);
  return (
    <AuthProvider
      trackEvent={(input) => trackEvent(input as TrackingEvent)}
      isUserSignedUp={isUserSignedUp ?? false}
      researchLifeConfiguration={{
        baseUrl: process.env.NEXT_PUBLIC_SSO_API_BASE_URL as string,
        product: process.env.NEXT_PUBLIC_SSO_PRODUCT as string,
      }}
      authPageConfiguration={{
        url: {
          loginUrl: '/login',
          signUpUrl: '/sign-up',
          forgotPasswordUrl: '/forgot-password',
        },
        logo: <PreflightLogo style={{ maxHeight: 45 }} />,
        linkWrapper: (href, component, email) => {
          return (
            <LinkWrapper email={email} href={href}>
              {component}
            </LinkWrapper>
          );
        },
      }}
      authContextSetup={{
        getUser: async () => {
          const token = localStorage.getItem(AUTH_TOKEN_KEY);

          if (!token) {
            return Promise.reject('Unable to get user without auth token');
          }

          const [user] = await Promise.all([
            preflightCoreApiClient.getUser(token),
            signInNext(token),
          ]);

          return user;
        },
        getSsoToken: (credentials) =>
          getSsoToken({
            baseUrl: BASE_URL,
            product: PRODUCT,
            credentials,
          }),
      }}
      authContextListener={{
        onUserCreated: async () => {
          //We should probably add big int id to the user at this point, if we have the backend end point
        },
        onLoggedIn: () => {
          setCookie(IS_USER_SIGNED_UP_COOKIE_NAME, 'true', {
            domain: props.domain,
            expires: getDateAYearFromNow(),
          });
        },
        onGotUser: (user) => {
          const userDetails = {
            name: user.name,
            email: user.email,
            ssoId: user.ssoUserId,
          };
          specialEvents.signIn(userDetails);
        },
        onFailedToGetUser: () => {
          const token = localStorage.getItem(AUTH_TOKEN_KEY);
          if (token) {
            signOutNext().catch(console.error);
            preflightCoreApiClient.signOut().catch(console.error);
            localStorage.clear();
          }
        },
        onLoggedOut: () => {
          signOutNext().catch(console.error);
          preflightCoreApiClient.signOut().catch(console.error);
          localStorage.clear();
        },
      }}
      socialSignIn={{
        enabled: process.env.NEXT_PUBLIC_ENABLE_SOCIAL_SIGNIN,
        useGoogleOAuth: true,
        google: {
          clientId: process.env.NEXT_PUBLIC_GOOGLE_OAUTH_CLIENT_ID,
        },
      }}
    >
      {props.children}
    </AuthProvider>
  );
};
